import { useWorkspace } from 'components/providers'
import { IntegrationTool, IntegrationToolInfo, IntegrationType, IWorkspaceIntegration } from 'types'
import adpLogo from '../assets/integration_logos/adp.svg'
import hubSpotLogo from '../assets/integration_logos/hubspot.svg'
import icimsLogo from '../assets/integration_logos/icims.svg'
import ifttLogo from '../assets/integration_logos/iftt.svg'
import salesforceLogo from '../assets/integration_logos/salesforce.svg'
import slackLogo from '../assets/integration_logos/slack.svg'
import microsoftTeamsLogo from '../assets/integration_logos/teams.svg'
import workdayLogo from '../assets/integration_logos/workday.svg'
import zapierLogo from '../assets/integration_logos/zapier.svg'

const {
  VITE_SALESFORCE_CONSUMER_KEY = '',
  VITE_SALESFORCE_CALLBACK_URL = '',
  VITE_HUBSPOT_CLIENT_ID = '',
  VITE_HUBSPOT_REDIRECT_URI = '',
} = import.meta.env

export const integrationTools: IntegrationToolInfo[] = [
  { name: 'HubSpot', type: 'CRM' },
  { name: 'Salesforce', type: 'CRM' },
  { name: 'ADP', type: 'ATS' },
  { name: 'iCIMS', type: 'ATS' },
  { name: 'Workday', type: 'ATS' },
  { name: 'Microsoft Teams', type: 'Productivity' },
  { name: 'Slack', type: 'Productivity' },
  { name: 'Zapier', type: 'Automation' },
  { name: 'IFTT', type: 'Automation' },
]

export function integrationToolDescription(type: IntegrationType) {
  switch (type) {
    case 'CRM':
      return 'This integration allows for seamless data flow and enhanced customer relationship management.'
    case 'ATS':
      return 'This integration allows for seamless data flow and enhanced candidate management.'
    case 'Productivity':
      return 'This integration allows for seamless data flow to your productivity tools.'
    case 'Automation':
      return 'This integration allows for seamless data flow to your automation tools.'
  }
}

export function useWorkspaceIntegrations(type: IntegrationType) {
  const { workspace } = useWorkspace()
  return workspace?.workspaceIntegrations?.filter(i => i.tool.type === type)
}

export function useActiveWorkspaceIntegration(type: IntegrationType) {
  const { workspace } = useWorkspace()
  return workspace?.workspaceIntegrations?.find(
    i => i.tool.type === type && i.status === 'connected'
  )
}

export function useIntegrationForConfiguration() {
  const { workspace } = useWorkspace()
  return workspace?.workspaceIntegrations?.find(i => i.status === 'needs-mapping')
}

export function updateWorkspaceIntegration(
  tool: IntegrationTool,
  body: Partial<IWorkspaceIntegration>
) {
  const { workspace, updateWorkspace } = useWorkspace()
  const updatedIntegrations =
    workspace?.workspaceIntegrations?.map(integration =>
      integration.tool.name === tool ? { ...integration, ...body } : integration
    ) || []

  updateWorkspace({ workspaceIntegrations: updatedIntegrations })
}

export function connectionStatusRenderer(status: string) {
  switch (status) {
    case 'connected':
      return { color: 'green.500', text: 'Connected' }
    case 'disconnected':
      return { color: 'gray.400', text: 'Disconnected' }
    case 'needs-mapping':
      return { color: 'red.500', text: 'Mapping required' }
    case 'error':
      return { color: 'red.500', text: 'Error' }
    case 'not-set':
      return { color: 'gray.500', text: 'Setup required' }
  }
}

export function connectToIntegration(workspaceId: string, tool: IntegrationTool) {
  let authUrl = ''
  switch (tool) {
    case 'Salesforce': {
      const salesforceAuthUrl = new URL('https://login.salesforce.com/services/oauth2/authorize')
      salesforceAuthUrl.searchParams.set('response_type', 'code')
      salesforceAuthUrl.searchParams.set('client_id', VITE_SALESFORCE_CONSUMER_KEY)
      salesforceAuthUrl.searchParams.set('redirect_uri', VITE_SALESFORCE_CALLBACK_URL)
      salesforceAuthUrl.searchParams.set('scope', 'refresh_token api openid')
      salesforceAuthUrl.searchParams.set('state', workspaceId)
      authUrl = salesforceAuthUrl.toString()
      break
    }

    case 'HubSpot': {
      const hubspotAuthUrl = new URL('https://app.hubspot.com/oauth/authorize')
      hubspotAuthUrl.searchParams.set('client_id', VITE_HUBSPOT_CLIENT_ID)
      hubspotAuthUrl.searchParams.set('redirect_uri', VITE_HUBSPOT_REDIRECT_URI)
      hubspotAuthUrl.searchParams.set(
        'scope',
        'crm.objects.contacts.write crm.objects.contacts.read'
      )
      hubspotAuthUrl.searchParams.set('state', workspaceId)
      authUrl = hubspotAuthUrl.toString()
      break
    }

    default:
      return
  }

  if (authUrl) window.location.href = authUrl
}

export function integrationLogoRenderer(tool: IntegrationTool) {
  switch (tool) {
    case 'ADP':
      return adpLogo

    case 'iCIMS':
      return icimsLogo

    case 'Workday':
      return workdayLogo

    case 'HubSpot':
      return hubSpotLogo

    case 'Salesforce':
      return salesforceLogo

    case 'Microsoft Teams':
      return microsoftTeamsLogo

    case 'Slack':
      return slackLogo

    case 'Zapier':
      return zapierLogo

    case 'IFTT':
      return ifttLogo
  }
}
